import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';
import type { InstanceOutputV1 } from '@seeqdev/api/model/InstanceOutputV1';
import type { TemplateApplicationMappingV1 } from '@seeqdev/api/model/TemplateApplicationMappingV1';

/**
 * Searches properties for one matching 'Created By Model Instance' and returns the value
 */
export const getTemplateInstanceIdFromProps = (
  properties: { name?: string; value?: string }[] | undefined,
  includeRetired?: boolean,
) => {
  if (properties) {
    const createdByTemplateInstanceProp = properties.find(
      (property) => property.name === SeeqNames.Properties.CreatedByModelInstance,
    );
    const retired = properties.find((property) => property.name === SeeqNames.Properties.InstanceRetired);
    if (createdByTemplateInstanceProp && (!retired || includeRetired)) {
      return createdByTemplateInstanceProp.value;
    }
  }
};

/**
 * Carries forward an instance's input mappings onto a template's input features for
 * rehydrating the template-apply tool during edit or upgrade.
 *
 * Matches features by canonicalId first (stable across template versions), then falls
 * back to feature id. Unmatched features are returned with no input values.
 */
export const mapInstanceInputsToFeatures = (
  instanceData: InstanceOutputV1,
  templateInputFeatures: InputFeatureV1[],
): InputFeatureV1[] => {
  const inputsByFeatureId = new Map((instanceData.inputs ?? []).map((input) => [input.featureId, input]));
  const inputsByCanonicalId = new Map(
    (instanceData.inputFeatures ?? [])
      .filter((f) => f.canonicalId && f.id)
      .map((f) => [f.canonicalId!, inputsByFeatureId.get(f.id!)]),
  );

  return templateInputFeatures.map((feature) => {
    const instanceInput =
      (feature.canonicalId && inputsByCanonicalId.get(feature.canonicalId)) || inputsByFeatureId.get(feature.id);
    // Smuggles inputItem, inputNumeric, etc. onto the InputFeatureV1 shape.
    // template.store.ts setTemplateOutput reads them as ModelInput, which declares those fields.
    return { ...feature, ...extractInputValues(instanceInput, feature.name ?? '') };
  });
};

const extractInputValues = (instanceInput: TemplateApplicationMappingV1 | undefined, featureName: string) => {
  const values: Record<string, string | number | boolean> = { name: featureName };
  if (instanceInput?.itemIdInput !== undefined) values.inputItem = instanceInput.itemIdInput;
  if (instanceInput?.numericInput !== undefined) values.inputNumeric = instanceInput.numericInput;
  if (instanceInput?.numericInputUom !== undefined) values.inputNumericUom = instanceInput.numericInputUom;
  if (instanceInput?.textInput !== undefined) values.inputText = instanceInput.textInput;
  if (instanceInput?.booleanInput !== undefined) values.inputBoolean = instanceInput.booleanInput;
  return values;
};
